import { clearSession, session, setToken } from '../store/session';
import type {
  ActivateResult,
  Address,
  BirthdayStatus,
  CardInspectResult,
  JsSdkConfig,
  LedgerEntry,
  Product,
  ProfileInput,
  ProfileProgress,
  Redemption,
  ServiceMessage,
} from './types';
import type { DoctorProfile, PointsSummary } from '../store/session';

const BASE = import.meta.env.VITE_API_BASE || '/api';

/** 后端领域错误的形状，前端按 code 分支，不按文案匹配 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail?: Record<string, unknown>,
  ) {
    super(message);
  }
}

/** 401 时通知外层跳登录。由 main.ts 注册，避免 client 直接依赖 router。 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  // FormData 要让浏览器自己带 boundary，不能手动写 content-type
  const isForm = body instanceof FormData;
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method,
      headers: {
        ...(isForm ? {} : { 'content-type': 'application/json' }),
        ...(session.token ? { authorization: `Bearer ${session.token}` } : {}),
      },
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK', '网络不太稳定，请重试');
  }

  if (res.status === 401) {
    clearSession();
    onUnauthorized?.();
    throw new ApiError(401, 'UNAUTHORIZED', '登录已过期，请重新进入');
  }

  const text = await res.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!res.ok) {
    const p = payload as
      | { code?: string; message?: string | string[]; detail?: Record<string, unknown> }
      | null;
    // NestJS 的 ValidationPipe 会把 message 塞成数组
    const msg = Array.isArray(p?.message) ? p.message[0] : p?.message;
    throw new ApiError(
      res.status,
      p?.code || 'UNKNOWN',
      msg || '操作失败，请稍后重试',
      p?.detail,
    );
  }

  return payload as T;
}

export const api = {
  /**
   * 用一次性票据换 token。
   *
   * 票据来自微信回调重定向的 fragment，两分钟过期且只能用一次 ——
   * 换完必须立刻把它从地址栏抹掉（见 bootstrapSession）。
   */
  async exchangeTicket(ticket: string) {
    const r = await request<{ token: string; kind: 'doctor' | 'admin' }>(
      'POST',
      '/auth/exchange',
      { ticket },
    );
    // 后端签的是同一个 JWT 格式，区别只在 subject：admin 走业务员端
    setToken(r.token, r.kind === 'admin' ? 'sales' : 'doctor');
    return r;
  },

  /** 开发期免微信登录，生产环境后端会拒绝 */
  async devLogin(openid: string, kind: 'doctor' | 'sales' = 'doctor') {
    const r = await request<{ token: string; doctor: DoctorProfile }>(
      'POST',
      '/auth/dev-login',
      { openid, kind },
    );
    setToken(r.token, kind === 'sales' ? 'sales' : 'doctor');
    return r;
  },

  me: () =>
    request<{ doctor: DoctorProfile; points: PointsSummary }>('GET', '/me'),

  /** 医生换头像；返回落盘后的可访问 URL */
  uploadImage: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return request<{ url: string; bytes: number; mime: string; name: string }>(
      'POST',
      '/me/uploads/image',
      fd,
    );
  },

  listAddresses: () => request<Address[]>('GET', '/me/addresses'),

  addAddress: (input: Omit<Address, 'id'>) =>
    request<Address>('POST', '/me/addresses', input),

  deleteAddress: (id: number) =>
    request<{ deleted: number }>('DELETE', `/me/addresses/${id}`),

  balance: () => request<PointsSummary>('GET', '/points/balance'),

  ledger: (limit = 30, offset = 0) =>
    request<LedgerEntry[]>('GET', `/points/ledger?limit=${limit}&offset=${offset}`),

  inspectCard: (cardNo: string) =>
    request<CardInspectResult>(
      'GET',
      `/cards/value/inspect?cardNo=${encodeURIComponent(cardNo)}`,
    ),

  activateCard: (cardNo: string, activationCode?: string) =>
    request<ActivateResult>('POST', '/cards/value/activate', {
      cardNo,
      ...(activationCode ? { activationCode } : {}),
    }),

  products: () => request<Product[]>('GET', '/products'),

  product: (id: number) => request<Product>('GET', `/products/${id}`),

  redemptions: () => request<Redemption[]>('GET', '/redemptions'),

  redemption: (id: number) => request<Redemption>('GET', `/redemptions/${id}`),

  createRedemption: (lines: { skuId: number; quantity: number }[], addressId: number) =>
    request<Redemption>('POST', '/redemptions', { lines, addressId }),

  cancelRedemption: (id: number, reason?: string) =>
    request<{ redemptionNo: string; refunded: number }>(
      'POST',
      `/redemptions/${id}/cancel`,
      { reason },
    ),

  /** 客服通道：看历史 / 发文字和图片 */
  serviceMessages: () => request<ServiceMessage[]>('GET', '/service/messages'),

  sendServiceMessage: (body: string, images: string[]) =>
    request<ServiceMessage>('POST', '/service/messages', { body, images }),

  /** 把这笔兑换单作为卡片发进客服通道（客服会自动回一条） */
  tagServiceOrder: (redemptionId: number) =>
    request<{ doctorId: number; redemptionId: number; created: boolean }>(
      'POST',
      '/service/order-tags',
      { redemptionId },
    ),

  /** 资料任务进度 */
  profileTasks: () => request<ProfileProgress>('GET', '/me/profile/tasks'),

  /** 提交资料。只传要改的字段，返回最新进度。 */
  saveProfile: (input: ProfileInput) =>
    request<ProfileProgress>('PATCH', '/me/profile', input),

  claimTask: (code: string) =>
    request<{ code: string; title: string; points: number; balanceAfter: number }>(
      'POST',
      '/me/profile/tasks/claim',
      { code },
    ),

  birthdayStatus: () => request<BirthdayStatus>('GET', '/me/profile/birthday'),

  claimBirthday: () =>
    request<{ points: number; year: number; balanceAfter: number }>(
      'POST',
      '/me/profile/birthday/claim',
    ),

  jsConfig: (url: string) =>
    request<JsSdkConfig>('GET', `/wechat/js-config?url=${encodeURIComponent(url)}`),
  // ---- 质保卡（支架路）----

  /**
   * 探一下这张质保卡值多少分。只读，不写任何数据 ——
   * 医生确认了分值再领，不做「扫到就直接入账」。
   */
  warrantyInspect: (cardId: string) =>
    request<{
      cardId: string;
      orderNo: string;
      points: number;
      claimable: boolean;
      claimed: { claimedAt: string; claimedBy: number; points: number } | null;
      /** 分值是从原单反查来的，不是这张卡直接绑的那一单 */
      viaRework: boolean;
      breakdown: Array<{ points: number; basis: string }>;
    }>('GET', `/cards/warranty/inspect?cardId=${encodeURIComponent(cardId)}`),

  /**
   * 领质保卡的积分。
   *
   * `rawScan` 是扫码得到的**原始文本**，手输时不传。拉不到订单时后端会拿它
   * 建核查工单交给客服 —— 卡号解析可能就是出错的那一环，只给解析结果，
   * 客服看不出这张卡实际长什么样。
   */
  warrantyClaim: (cardId: string, rawScan?: string) =>
    request<{
      cardId: string;
      points: number;
      balanceAfter: number;
      orderNo: string;
      viaRework: boolean;
      breakdown: Array<{ points: number; basis: string }>;
    }>('POST', '/cards/warranty/claim', { cardId, rawScan }),

  /** 申诉：这张卡的分被别人领走了 */
  warrantyAppeal: (cardId: string, rawScan?: string) =>
    request<{ requestId: number | null }>('POST', '/cards/warranty/appeal', {
      cardId,
      rawScan,
    }),
};

